import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { useThemeStore } from '@/stores/theme';
import type {
  User,
  Coordinator,
  Participant,
  AuthResponse,
  StaffLoginInput,
  RegisterInput,
  ResetPasswordInput,
  MessageResponse,
  UserUpdateInput,
  UserUnlockInput,
  ParticipantSuspendInput,
  ParticipantActivateInput,
  ParticipantResetPasswordInput,
  ListParticipantsResponse,
} from 'shared';

// Shape returned by GET /api/auth/me for staff.
interface MeResponse {
  user: User;
  coordinator: Coordinator | null;
}

// Web is staff-only — all auth calls target the /staff subtree.
const STAFF_BASE = '/api/auth/staff';

// -----------------------------------------------------------------------------
// Initial administrator bootstrap
// -----------------------------------------------------------------------------
// The backoffice ships with an empty users table on first install. Until a
// staff user exists we expose a one-shot endpoint to create the very first
// administrator. Once created, the server permanently refuses further calls
// (HTTP 409), so these hooks stop being useful the moment they succeed.

export interface InitialSetupStatus {
  needsSetup: boolean;
}

export interface InitialSetupInput {
  name: string;
  email: string;
  phone: string;
  password: string;
}

export function useInitialSetupStatus(enabled = true) {
  return useQuery({
    queryKey: ['auth', 'setup-status'],
    queryFn: () =>
      api.get<InitialSetupStatus>('/api/auth/setup/status', { skipAuth: true }),
    // Refetched on demand by the login page after a successful setup call.
    staleTime: 0,
    gcTime: 0,
    retry: false,
    enabled,
  });
}

export function useCreateInitialAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: InitialSetupInput) =>
      api.post<{ message: string }>('/api/auth/setup', data, { skipAuth: true }),
    onSuccess: () => {
      // Force the login page to refetch and hide the modal forever.
      queryClient.invalidateQueries({ queryKey: ['auth', 'setup-status'] });
    },
  });
}


// Login mutation
export function useLogin() {
  const setAuth = useAuthStore((state) => state.setAuth);
  const setTheme = useThemeStore((state) => state.setTheme);

  return useMutation({
    mutationFn: (data: StaffLoginInput) =>
      api.post<AuthResponse>(`${STAFF_BASE}/login`, data, { skipAuth: true }),
    onSuccess: (response) => {
      setAuth(response, 'staff');
      // Sync theme preference from server
      if (response.user.themePreference) {
        setTheme(response.user.themePreference);
      }
    },
  });
}

// Register mutation.
// The backend now always responds with HTTP 202 + { message } when the account
// goes into pending-approval state.  We guard against calling setAuth with a
// non-auth payload so the auth store is never corrupted.
export function useRegister() {
  const setAuth = useAuthStore((state) => state.setAuth);

  return useMutation({
    mutationFn: (data: RegisterInput) =>
      api.post<AuthResponse | { message: string }>('/api/auth/register', data, { skipAuth: true }),
    onSuccess: (response) => {
      // Only call setAuth if the server returned real tokens (future-proof path).
      if ('accessToken' in response && (response as AuthResponse).accessToken) {
        setAuth(response as AuthResponse, 'staff');
      }
      // Otherwise (pending approval) the component handles the UI — do nothing here.
    },
  });
}

// Logout mutation
export function useLogout() {
  const { refreshToken, kind, logout } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      const endpoint = kind ? `/api/auth/${kind}/logout` : '/api/auth/logout';
      return api.post(endpoint, { refreshToken });
    },
    onSettled: () => {
      logout();
      queryClient.clear();
    },
  });
}

// Get current user (with coordinator profile, if any)
export function useCurrentUser() {
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['currentUser'],
    queryFn: () => api.get<MeResponse>('/api/auth/me'),
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Reset password mutation
export function useResetPassword() {
  return useMutation({
    mutationFn: (data: ResetPasswordInput) =>
      api.post<MessageResponse>(`${STAFF_BASE}/reset-password`, data, { skipAuth: true }),
  });
}

// Update current user
export function useUpdateUser() {
  const { user, setUser } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UserUpdateInput) =>
      api.put<User>(`/api/users/${user?.id}`, data),
    onSuccess: (updatedUser) => {
      setUser(updatedUser);
      queryClient.setQueryData(['currentUser'], updatedUser);
    },
  });
}

// List users (admin)
export function useListUsers(limit = 100, offset = 0) {
  return useQuery({
    queryKey: ['users', limit, offset],
    queryFn: () =>
      api.get<{ users: User[]; total: number; limit: number; offset: number }>(
        `/api/users?limit=${limit}&offset=${offset}`
      ),
    staleTime: 1000 * 30, // 30 seconds
  });
}

// Unlock user (admin)
export function useUnlockUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: number; data: UserUnlockInput }) =>
      api.post<User>(`/api/users/${userId}/unlock`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

// List participants (admin) — used for search in modals
export function useListParticipants(limit = 500, offset = 0) {
  return useQuery({
    queryKey: ['participants', limit, offset],
    queryFn: () =>
      api.get<ListParticipantsResponse>(
        `/api/participants?limit=${limit}&offset=${offset}`
      ),
    staleTime: 1000 * 30, // 30 seconds
  });
}

// Suspend participant (admin)
export function useSuspendParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ participantId, data }: { participantId: number; data: ParticipantSuspendInput }) =>
      api.post<Participant>(`/api/participants/${participantId}/suspend`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['participants'] });
    },
  });
}

// Activate (reactivate) a suspended participant (admin)
export function useActivateParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ participantId, data }: { participantId: number; data: ParticipantActivateInput }) =>
      api.post<Participant>(`/api/participants/${participantId}/activate`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['participants'] });
    },
  });
}

// Reset a participant's password (admin or coordinator)
export function useResetParticipantPassword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ participantId, data }: { participantId: number; data: ParticipantResetPasswordInput }) =>
      api.post<Participant>(`/api/participants/${participantId}/reset-password`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['participants'] });
    },
  });
}
